import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Check, Loader2, ShieldAlert } from 'lucide-react';

export const KIND_STYLE = {
  customer: 'bg-amber-50 border-amber-200 text-amber-800',
  prospect: 'bg-amber-50 border-amber-200 text-amber-800',
  pricing: 'bg-rose-50 border-rose-200 text-rose-800',
  person: 'bg-violet-50 border-violet-200 text-violet-800',
};
export const KIND_LABEL = { customer: 'client name', prospect: 'prospect', pricing: 'pricing', person: 'person / contact' };

/** Colour-coded sensitive-content hit chips (client names amber, pricing rose, people violet). */
export const HitChips = ({ hits, testId }) => (
  <span className="flex flex-wrap gap-1" data-testid={testId}>
    {(hits || []).map((h, i) => (
      <span key={i} className={`rounded-full border px-2 py-0.5 text-[10px] ${KIND_STYLE[h.kind] || KIND_STYLE.customer}`}
        title={`${h.source === 'ai' ? 'AI' : h.source === 'list' ? 'Known list' : 'Pattern'} · ${KIND_LABEL[h.kind] || h.kind}`}>
        {h.name}{h.replacement ? <> → <i>{h.replacement}</i></> : <> · <i>edit manually</i></>}
      </span>
    ))}
  </span>
);

/** Promotion gate: an RFP answer heading for the library contains client names / pricing / people — the reviewer picks the library wording. The RFP answer is never changed. */
export const LibraryWordingDialog = ({ review, onConfirm, onCancel }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(null);
  useEffect(() => { if (review) setBody(review.proposed_body || review.answer || ''); }, [review]);
  const go = async (force) => {
    setBusy(force ? 'force' : 'use');
    try { await onConfirm(force ? review.answer : body, force); } finally { setBusy(null); }
  };
  const hasPricing = (review?.hits || []).some((h) => h.kind === 'pricing');
  return (
    <Dialog open={!!review} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto bg-white" data-testid="library-wording-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040] flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" />Choose the library wording</DialogTitle>
          <DialogDescription data-testid="library-wording-message">{review?.message} The RFP answer itself stays exactly as written — only the text below enters the library.</DialogDescription>
        </DialogHeader>
        {review && (
          <div className="space-y-3 text-xs">
            <HitChips hits={review.hits} testId="library-wording-hits" />
            {hasPricing && <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded px-2 py-1" data-testid="library-wording-pricing-note">Pricing figures aren't rewritten automatically — remove or generalise them in the text below before continuing.</p>}
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Library wording{review.proposed_body ? ' (generic rewrite proposed — edit as needed)' : ''}</p>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="text-xs min-h-[220px]" data-testid="library-wording-body" />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={onCancel} disabled={!!busy} className="h-8 text-xs" data-testid="library-wording-cancel">Cancel</Button>
              <Button size="sm" variant="outline" onClick={() => go(true)} disabled={!!busy} className="h-8 text-xs" title="False positive — put the answer in the library exactly as written" data-testid="library-wording-force">{busy === 'force' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Keep as-is (false positive)'}</Button>
              <Button size="sm" onClick={() => go(false)} disabled={!!busy || !body.trim()} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="library-wording-use">{busy === 'use' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}Use this wording in the library</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
